import { useMemo } from 'react';

/**
 * useCourseProgress
 * ------------------
 * Pure derivation of "Lesson 3 of 42"-style progress counters from a course
 * outline that the app already holds in memory. No API calls, no
 * localStorage access, no side effects — input in, memoized object out.
 *
 * TERMINOLOGY (read this before touching this file):
 * The outline the backend returns nests things as `outline.lessons[].sections[]`,
 * but that naming is misleading for UI purposes:
 *   - An outline "lesson" is actually a MODULE (a group of readable units).
 *   - An outline "section" is what the UI calls a "lesson" (a single readable
 *     unit: markdown/html/text/audio/video/quiz/knowledge-check).
 * To avoid perpetuating the confusion, this hook's internal vocabulary and
 * return shape use "module" for `outline.lessons[]` entries and "section"
 * for `outline.lessons[].sections[]` entries. Fields that must line up with
 * the raw outline (e.g. `moduleId` = `lessonId`) are commented accordingly.
 * The mockup's "Lesson 3 of 42" counts SECTIONS (flattened across all
 * modules), not API "lessons" (modules) — that's `totalSections`/`currentIndex`
 * below, not a module count.
 *
 * OUTLINE SHAPE (confirmed by reading useCourseLearn.js and CourseLearnView.jsx):
 *   {
 *     courseId, courseTitle, courseTag, difficulty,   // NB: courseTitle, not title
 *     lessons: [                      // modules
 *       {
 *         lessonId, order, title,
 *         sections: [                 // sections (UI "lessons")
 *           { sectionId, order, title, contentType, viewed?, passed? }
 *         ]
 *       }
 *     ]
 *   }
 * `contentType` on outline sections is uppercase in practice (confirmed via
 * `outlineSec.contentType === 'QUIZ'` checks in useCourseLearn.js), but this
 * hook still normalizes case defensively since `content.type` elsewhere in
 * the app arrives lowercase and the two get compared loosely.
 *
 * COMPLETION SEMANTICS (matches CourseLearnPage.jsx / CourseLearnView.jsx
 * exactly): a section counts as complete when `viewed === true`,
 * `passed === true`, OR it's present in the caller's optimistic
 * `localViewed` set (mirrors `localViewed`/`viewedSections` localStorage key
 * used elsewhere — this hook never reads localStorage itself, it's passed
 * in as `localViewed`).
 *
 * OUT OF SCOPE (deliberately not derived — see task notes):
 *   - Estimated time remaining: no duration/word-count field exists anywhere
 *     on the outline; computing one from content would be inventing data.
 *   - Locked/gated state: this product has no gating concept; every section
 *     is clickable today.
 *
 * @param {Object} params
 * @param {Object|null|undefined} params.outline - course outline (see shape above)
 * @param {string|null|undefined} params.selectedSectionId - currently selected section's id
 * @param {Set<string>} [params.localViewed] - optimistic set of sectionIds marked
 *   viewed locally (not yet reflected in `outline` from the server). Merged
 *   on top of `viewed`/`passed` so counts update immediately.
 *
 * @returns {{
 *   totalSections: number,
 *   currentIndex: number|null,          // 1-based position of selectedSectionId in flattened order; null if not found
 *   completedSections: number,
 *   percentComplete: number,            // 0-100, integer, never NaN
 *   totalQuizzes: number,               // sections with contentType === 'QUIZ'
 *   quizzesPassed: number,              // of those, passed === true
 *   perModule: Array<{
 *     moduleId: string,                 // outline lessonId
 *     order: number,
 *     title: string,
 *     sectionCount: number,
 *     hasQuiz: boolean,
 *     completedCount: number,
 *   }>,
 *   currentSection: Object|null,        // flattened entry for selectedSectionId, or null
 *   prevSection: Object|null,           // flattened entry adjacent (before), spanning module boundaries; null at the start
 *   nextSection: Object|null,           // flattened entry adjacent (after), spanning module boundaries; null at the end
 * }}
 */
export function useCourseProgress({ outline, selectedSectionId, localViewed }) {
  return useMemo(() => {
    const modules = Array.isArray(outline?.lessons) ? outline.lessons : [];

    const sortedModules = modules
      .slice()
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

    const isComplete = (section) => {
      if (!section) return false;
      if (section.viewed === true || section.passed === true) return true;
      if (localViewed && typeof localViewed.has === 'function') {
        return localViewed.has(section.sectionId);
      }
      return false;
    };

    const isQuiz = (section) =>
      String(section?.contentType || '').toUpperCase() === 'QUIZ';

    // Flatten modules -> sections, in module order then section order,
    // tagging each section with its owning module for boundary-spanning nav.
    const flat = [];
    const perModule = [];

    for (const mod of sortedModules) {
      const sections = Array.isArray(mod?.sections) ? mod.sections : [];
      const sortedSections = sections
        .slice()
        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

      let completedCount = 0;
      let hasQuiz = false;

      for (const section of sortedSections) {
        const complete = isComplete(section);
        if (complete) completedCount += 1;
        if (isQuiz(section)) hasQuiz = true;

        flat.push({
          ...section,
          moduleId: mod?.lessonId ?? null,
          moduleOrder: mod?.order ?? 0,
          completed: complete,
        });
      }

      perModule.push({
        moduleId: mod?.lessonId ?? null,
        order: mod?.order ?? 0,
        title: mod?.title ?? '',
        sectionCount: sortedSections.length,
        hasQuiz,
        completedCount,
      });
    }

    const totalSections = flat.length;
    const completedSections = flat.reduce((n, s) => n + (s.completed ? 1 : 0), 0);
    const percentComplete = totalSections > 0
      ? Math.round((completedSections / totalSections) * 100)
      : 0;

    const quizSections = flat.filter(isQuiz);
    const totalQuizzes = quizSections.length;
    const quizzesPassed = quizSections.reduce((n, s) => n + (s.passed === true ? 1 : 0), 0);

    const flatIndex = selectedSectionId
      ? flat.findIndex((s) => s.sectionId === selectedSectionId)
      : -1;

    const currentIndex = flatIndex >= 0 ? flatIndex + 1 : null;
    const currentSection = flatIndex >= 0 ? flat[flatIndex] : null;
    const prevSection = flatIndex > 0 ? flat[flatIndex - 1] : null;
    const nextSection = flatIndex >= 0 && flatIndex < flat.length - 1
      ? flat[flatIndex + 1]
      : null;

    return {
      totalSections,
      currentIndex,
      completedSections,
      percentComplete,
      totalQuizzes,
      quizzesPassed,
      perModule,
      currentSection,
      prevSection,
      nextSection,
    };
  }, [outline, selectedSectionId, localViewed]);
}

export default useCourseProgress;
